import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  FileText, 
  Image,
  Search, 
  Shield, 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  Trash2,
  Eye,
  Loader2,
  HardDrive,
  AlertTriangle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";

const API = "/api";

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-[#111] border border-[#222] rounded-xl p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[#666] text-xs mb-1">{title}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
    </div>
  </div>
);

export default function AdminFiles() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [storageStats, setStorageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [viewFile, setViewFile] = useState(null);
  const [deleteFile, setDeleteFile] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const adminEmail = localStorage.getItem("admin_email");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchFiles();
    fetchStorageStats();
  }, [navigate, page, search, typeFilter]);

  const fetchFiles = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "25" });
      if (search) params.append("search", search);
      if (typeFilter !== "all") params.append("file_type", typeFilter);

      const response = await fetch(`${API}/admin/files?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }

      const data = await response.json();
      setFiles(data.files || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch files");
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageStats = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/admin/files/storage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStorageStats(data);
    } catch (error) {
      console.error("Failed to fetch storage stats");
    }
  };

  const handleViewFile = async (fileId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/admin/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setViewFile(data);
    } catch (error) {
      toast.error("Failed to load file details");
    }
  };

  const handleDelete = async () => {
    if (!deleteFile) return;
    setDeleteLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/files/${deleteFile.file_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Delete failed");

      toast.success("File deleted");
      setDeleteFile(null);
      fetchFiles();
      fetchStorageStats();
    } catch (error) {
      toast.error("Failed to delete file");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/login");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric"
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isImage = (contentType) => contentType?.startsWith("image/");

  return (
    <div className="min-h-screen bg-[#0a0a0a]">

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-1">File Management</h1>
          <p className="text-[#666]">{total} total files</p>
        </div>

        {/* Storage Stats */}
        {storageStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard title="Total Files" value={storageStats.total_files} icon={FileText} color="bg-blue-600" />
            <StatCard title="Total Storage" value={`${storageStats.total_size_mb} MB`} icon={HardDrive} color="bg-purple-600" />
            <StatCard title="Images" value={storageStats.image_count} icon={Image} color="bg-green-600" />
            <StatCard title="Documents" value={storageStats.document_count} icon={FileText} color="bg-orange-600" />
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
            <Input
              placeholder="Search by filename..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 bg-[#111] border-[#333] text-white placeholder:text-[#555]"
            />
          </div>
          
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px] bg-[#111] border-[#333] text-white">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-[#333]">
              <SelectItem value="all" className="text-white">All Types</SelectItem>
              <SelectItem value="image" className="text-white">Images</SelectItem>
              <SelectItem value="document" className="text-white">Documents</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0a0a] border-b border-[#222]">
                <tr>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">File</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">User</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Type</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Size</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Uploaded</th>
                  <th className="text-right text-[#888] text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-[#555] mx-auto" />
                    </td>
                  </tr>
                ) : files.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-[#555]">No files found</td>
                  </tr>
                ) : (
                  files.map((file) => (
                    <tr key={file.file_id} className="border-b border-[#222] hover:bg-[#1a1a1a] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {isImage(file.content_type) ? (
                            <Image className="w-4 h-4 text-green-400" />
                          ) : (
                            <FileText className="w-4 h-4 text-blue-400" />
                          )}
                          <span className="text-white truncate max-w-[200px]">{file.original_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#888] text-sm">{file.user_email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full border ${
                          isImage(file.content_type) 
                            ? "bg-green-500/20 text-green-400 border-green-500/30"
                            : "bg-blue-500/20 text-blue-400 border-blue-500/30"
                        }`}>
                          {isImage(file.content_type) ? "Image" : "Document"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[#888]">{formatSize(file.file_size)}</td>
                      <td className="px-6 py-4 text-[#888] text-sm">{formatDate(file.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewFile(file.file_id)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteFile(file)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#222]">
              <p className="text-[#666] text-sm">Page {page} of {totalPages}</p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-[#888] hover:text-white disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="text-[#888] hover:text-white disabled:opacity-50">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* View File Modal */}
      <Dialog open={!!viewFile} onOpenChange={() => setViewFile(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewFile && isImage(viewFile.content_type) ? (
                <Image className="w-5 h-5 text-green-400" />
              ) : (
                <FileText className="w-5 h-5 text-blue-400" />
              )}
              File Details
            </DialogTitle>
          </DialogHeader>
          
          {viewFile && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#666] text-xs mb-1">Filename</p>
                  <p className="text-white">{viewFile.original_name}</p>
                </div>
                <div>
                  <p className="text-[#666] text-xs mb-1">Size</p>
                  <p className="text-white">{formatSize(viewFile.file_size)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#666] text-xs mb-1">User</p>
                  <p className="text-white">{viewFile.user_email}</p>
                </div>
                <div>
                  <p className="text-[#666] text-xs mb-1">Uploaded</p>
                  <p className="text-white">{formatDate(viewFile.created_at)}</p>
                </div>
              </div>
              <div>
                <p className="text-[#666] text-xs mb-1">Content Type</p>
                <p className="text-white">{viewFile.content_type}</p>
              </div>
              <div>
                <p className="text-[#666] text-xs mb-1">File ID</p>
                <p className="text-white font-mono text-sm">{viewFile.file_id}</p>
              </div>
              
              {viewFile.extracted_text && (
                <div>
                  <p className="text-[#666] text-xs mb-2">Extracted Content ({viewFile.text_length} chars)</p>
                  <ScrollArea className="h-[200px] bg-[#0a0a0a] rounded-lg p-3">
                    <p className="text-[#888] text-sm whitespace-pre-wrap">{viewFile.extracted_text}</p>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteFile} onOpenChange={() => setDeleteFile(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete File
            </DialogTitle>
            <DialogDescription className="text-[#888]">
              {deleteFile && <>Delete "{deleteFile.original_name}"? This cannot be undone.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDeleteFile(null)} className="text-[#888] hover:text-white">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleteLoading} className="bg-red-600 hover:bg-red-700">
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
